// Générer les dotations (/immobilisations/dotations).
// Aperçu des dotations planifiées → génération des écritures OD EN ATTENTE,
// puis validation/rejet avant intégration définitive au journal OD.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatErreur } from "@/lib/api";
import { roleEffectif } from "@/lib/roles";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calculator, Loader2, CheckCircle2, X } from "lucide-react";

const fmt = (n) => (n != null ? Math.round(n).toLocaleString("fr-FR") : "0");
const anneeCourante = new Date().getFullYear();
const annees = [anneeCourante - 1, anneeCourante, anneeCourante + 1];

export default function ImmobilisationsDotations() {
  const { user } = useAuth();
  const peutGerer = ["gerant", "administrateur"].includes(roleEffectif(user));
  const [exercice, setExercice] = useState(String(anneeCourante));
  const [apercu, setApercu] = useState(null);
  const [attente, setAttente] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [envoi, setEnvoi] = useState(false);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const [a, q] = await Promise.all([
        api.get("/immobilisations/dotations/apercu", { params: { exercice: parseInt(exercice, 10) } }),
        api.get("/immobilisations/od-attente/liste"),
      ]);
      setApercu(a.data);
      setAttente(q.data.od_attente || []);
    } catch { setApercu(null); setAttente([]); }
    finally { setChargement(false); }
  }, [exercice]);

  useEffect(() => { charger(); }, [charger]);

  const generer = async () => {
    setEnvoi(true);
    try {
      const { data } = await api.post(`/immobilisations/dotations/generer?exercice=${parseInt(exercice, 10)}`);
      toast.success(`${data.od_generees} écriture(s) OD générée(s) en attente de validation.`);
      await charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail) || "Génération impossible.");
    } finally { setEnvoi(false); }
  };

  const valider = async (id) => {
    try {
      const { data } = await api.post(`/immobilisations/od-attente/${id}/valider`);
      toast.success(`Écriture ${data.numero} validée et intégrée au journal OD.`);
      await charger();
    } catch (e) { toast.error(formatErreur(e.response?.data?.detail) || "Validation impossible."); }
  };

  const rejeter = async (id) => {
    try {
      await api.delete(`/immobilisations/od-attente/${id}`);
      toast.success("Écriture rejetée.");
      await charger();
    } catch (e) { toast.error(formatErreur(e.response?.data?.detail) || "Rejet impossible."); }
  };

  return (
    <div className="fenua-fade-up" data-testid="page-immobilisations-dotations">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-11 w-11 rounded-lg bg-[#E8F4F4] flex items-center justify-center">
          <Calculator className="h-6 w-6 text-[#1D3D3D]" />
        </div>
        <h1 data-testid="titre-section" className="text-3xl font-black tracking-tight text-[#1D3D3D]">Générer les dotations</h1>
      </div>

      {/* Aperçu + génération */}
      <div className="bg-white border border-[#4A8A8A]/20 rounded-lg p-5">
        <div className="flex items-center gap-3 mb-4">
          <span className="text-sm font-semibold text-[#1D3D3D]">Exercice</span>
          <Select value={exercice} onValueChange={setExercice}>
            <SelectTrigger data-testid="dotations-exercice" className="w-[140px] h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{annees.map((a) => <SelectItem key={a} value={String(a)}>{a}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        {chargement ? (
          <div className="flex justify-center py-6"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
        ) : (
          <div className="flex flex-wrap items-center justify-between gap-4" data-testid="dotations-apercu">
            <p className="text-sm text-[#1D3D3D]">
              <strong>{apercu?.nombre || 0}</strong> immobilisation(s) concernée(s) ·
              Dotations totales : <strong>{fmt(apercu?.total_dotations)}</strong> XPF
            </p>
            {peutGerer && (apercu?.nombre || 0) > 0 && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button data-testid="bouton-generer-dotations" disabled={envoi} className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
                    {envoi ? <Loader2 className="h-4 w-4 animate-spin" /> : `Générer les écritures OD de l'exercice ${exercice}`}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Générer les dotations {exercice} ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {apercu.nombre} immobilisation(s) concernée(s). Dotations totales : {fmt(apercu.total_dotations)} XPF.
                      Les écritures OD seront créées <strong>en attente de validation</strong>. Confirmer la génération ?
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction data-testid="confirmer-generation" onClick={generer} className="bg-[#1D3D3D] hover:bg-[#2D5A5A]">Confirmer</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        )}
      </div>

      {/* File d'attente de validation */}
      <div className="mt-6 bg-white border border-[#4A8A8A]/20 rounded-lg overflow-hidden">
        <div className="px-4 py-2 bg-[#E8F4F4] font-semibold text-sm text-[#1D3D3D]">
          Écritures OD en attente de validation
        </div>
        <table className="w-full text-sm" data-testid="table-od-attente">
          <thead>
            <tr className="text-left text-[#1D3D3D]/60 border-b">
              <th className="px-4 py-2">Type</th><th className="px-4 py-2">Libellé</th>
              <th className="px-4 py-2">Date</th><th className="px-4 py-2 text-right">Montant</th>
              <th className="px-4 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {attente.length === 0 && <tr><td colSpan={5} className="px-4 py-6 text-center text-[#1D3D3D]/60">Aucune écriture en attente.</td></tr>}
            {attente.map((od) => (
              <tr key={od.id} data-testid={`od-attente-${od.id}`} className="border-b border-[#4A8A8A]/10">
                <td className="px-4 py-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-[#4A8A8A]/15">{od.type === "dotation" ? "Dotation" : "Cession"}</span>
                </td>
                <td className="px-4 py-2 text-[#1D3D3D]">{od.libelle}</td>
                <td className="px-4 py-2 text-[#1D3D3D]/70">{od.date_operation}</td>
                <td className="px-4 py-2 text-right font-medium">{fmt(od.total_debit)}</td>
                <td className="px-4 py-2 text-right space-x-2">
                  {peutGerer && (
                    <>
                      <Button data-testid={`valider-od-${od.id}`} size="sm" onClick={() => valider(od.id)} className="bg-green-600 hover:bg-green-700 text-white">
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Valider
                      </Button>
                      <Button data-testid={`rejeter-od-${od.id}`} size="sm" variant="outline" onClick={() => rejeter(od.id)} className="text-red-600 border-red-200">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
